import type { FormlyFieldConfig } from '@ngx-formly/core';

import { FormHelper } from '../../../../features/admin/modules/features/form-builder/form.util';
import type { ReviewValueContext } from '../review-value.types';
import type { EntryRouteResolver, TaxonomyLabelStore, TaxonomyOption } from './readonly.types';

// The types live in `readonly.types.ts` with the rest of read mode's vocabulary (project
// convention: types in a `*.types.ts`). Re-exported here because they are this module's
// signature, so existing importers keep one obvious place to get them from.
export type { EntryRouteResolver, LinkedEntryEnvelope, TaxonomyLabelStore, TaxonomyOption } from './readonly.types';

/**
 * Label lookup for taxonomy-backed option sets — the option labels a persisted form cannot
 * carry.
 *
 * `FormService` strips optionsArray-backed fields to `options: []` before persisting, so every
 * surface that reads a SAVED `generatedForm` (the onboarding-review card) or a field whose live
 * options were never refilled sees a bare slug/id where the user picked a label. Resolution stays
 * UNFILTERED on purpose (`FormHelper.filterTaxonomyOptions` is for the pick-list): an answer that
 * has since been excluded from the field still has to read as what it was. Module-backed sets are
 * not resolved here; their answers are stored as linked-entry objects, which the shared formatter
 * labels from `selectedInput`/`name`.
 *
 * `cache` is keyed by `ownerModuleId|taxonomyId` and is OWNED BY THE CALLER, which is what keeps
 * its lifetime honest: taxonomies can change between renders, so a cache that outlived one
 * render pass would serve stale labels. A multi-select answer recurses the resolver per item and
 * sibling fields routinely share a taxonomy, so without a cache `FormHelper.taxonomyToOptions`
 * (a full array rebuild) reran per value lookup instead of once per distinct taxonomy. The owner
 * module must be part of the key: two fields can share a `taxonomyId` while pointing at
 * different owner modules (different `moduleCode`), and keying on `taxonomyId` alone would let
 * the second field read back the first's resolved — or empty — options.
 *
 * Returns `undefined` when the field has no taxonomy-backed set, so `ReviewValueContext` carries
 * no resolver at all for the (many) fields that do not need one.
 */
export function createTaxonomyOptionResolver(
  store: TaxonomyLabelStore,
  optionsArray: unknown,
  cache: Map<string, TaxonomyOption[]> = new Map()
): ((value: string) => string | null) | undefined {
  if (!Array.isArray(optionsArray)) {
    return undefined;
  }
  const taxonomySets = optionsArray.filter(
    (set): set is { taxonomyId?: string; moduleCode?: string } =>
      !!set && typeof set === 'object' && (set as { optionsType?: string }).optionsType === 'taxonomy'
  );
  if (taxonomySets.length === 0) {
    return undefined;
  }

  return (value: string): string | null => {
    const taxonomies = store.taxonomies();
    const moduleCodeMapper = store.moduleCodeMapper();
    for (const set of taxonomySets) {
      const ownerModuleId = set.moduleCode ? moduleCodeMapper[set.moduleCode]?.id : undefined;
      const cacheKey = `${ownerModuleId ?? ''}|${set.taxonomyId ?? ''}`;
      let options = cache.get(cacheKey);
      if (!options) {
        options = FormHelper.taxonomyToOptions(taxonomies, ownerModuleId, set.taxonomyId);
        cache.set(cacheKey, options);
      }
      const match = options.find(option => option.value.slug === value || option.value.id === value);
      if (match?.label) {
        return match.label;
      }
    }
    return null;
  };
}

/**
 * `ReviewValueContext` for ONE live Formly field — what a read display needs to turn the field's
 * value into text.
 *
 * The store comes from `field.options.formState.modulesStore`, which `DetailsService` publishes
 * on every entry form (details.service.ts, `formState`). Reading it from the field instead of
 * injecting `ModulesStore` keeps the displays presentational: in Storybook and in standalone
 * forms there is no store, the resolver stays `undefined`, and the formatter falls back to the
 * stored text — a story renders the raw value instead of failing to construct.
 *
 * Each call gets its own cache: one field, one render. Callers that walk many fields in one pass
 * (the onboarding-review card) share a cache across the walk by calling
 * `createTaxonomyOptionResolver` directly.
 */
export function displayContextFor(field: FormlyFieldConfig): ReviewValueContext {
  const props = field.props ?? {};
  const store = (field.options?.formState as { modulesStore?: TaxonomyLabelStore } | undefined)?.modulesStore;
  return {
    type: field.type as string | undefined,
    options: props.options,
    optionsArray: props['optionsArray'],
    resolveOptionLabel: store ? createTaxonomyOptionResolver(store, props['optionsArray']) : undefined,
    addonLeft: addonText(props.addonLeft),
    addonRight: addonText(props.addonRight),
  };
}

/**
 * The printable half of an addon (`{ text?, icon? }` — number.component.ts / currency.component.ts).
 * An icon-only addon yields nothing: a read row is one line of text, and a Material ligature name
 * ('scale') printed as a word would be worse than no unit at all.
 */
function addonText(addon: unknown): string | undefined {
  const text = (addon as { text?: unknown } | undefined)?.text;
  return typeof text === 'string' && text.trim() !== '' ? text.trim() : undefined;
}

/**
 * The route builder for linked answers, as published by the host page on `formState`
 * (`DetailsService`) — `undefined` where no page published one.
 *
 * Same rule as the modules store above: the read displays stay presentational and read their
 * context OFF THE FIELD, so a Storybook story or a standalone form renders the answer as text
 * instead of failing to construct. The type check is not ceremony — `formState` is a shared bag
 * several features write into, and a non-function under this key has to read as "no resolver"
 * rather than blow up the row when it is called.
 */
export function entryRouteResolverOf(field: FormlyFieldConfig): EntryRouteResolver | undefined {
  const resolve = (field.options?.formState as { resolveEntryRoute?: unknown } | undefined)?.resolveEntryRoute;
  return typeof resolve === 'function' ? (resolve as EntryRouteResolver) : undefined;
}
